import { Loader2 } from 'lucide-react';
import { STRINGS } from '@/strings/strings';
import type { PreviewEnvelope } from '../types';

/**
 * Block 6 · 6.7 · Post-payment waiting screen (Sebastian ruling
 * 2026-09-09).
 *
 * Full rewrite of the pre-Block-6 three-dot stage indicator. Same
 * layout on phone + desktop, everything stacked vertically. Renders
 * for statuses `paid` / `rendering_full` / `qa_running` from
 * BookRoute, and also inline from BestellungErfolgRoute while it
 * polls the checkout status (Block 6.6 flash-screen removal — one
 * continuous wait screen from Stripe redirect through the paid
 * flip).
 *
 * Structure (top to bottom):
 *   1. Heading  "Vielen Dank für eure Bestellung"
 *   2. Body 1   "Gleich seht ihr euer Buch zum ersten Mal fertig…"
 *   3. Body 2   "In etwa fünf Minuten liegt es hier zur Freigabe…"
 *   4. Anchor image (from envelope.cover.imageUrl)
 *   5. Four preview images in a 2×2 grid (envelope.previewTiles).
 *      Same "Seite N im Buch" / "Eure Figuren" captions as Screen 4.
 *   6. Three plain lines: "Wir schreiben", "Wir zeichnen",
 *      "Wir kontrollieren jede Seite"
 *
 * NO progress bar. Over ~5 min a bar is either dishonest or
 * motionless — the tiles carry the screen. The customer looks at
 * what they already have.
 */

export interface PostPaymentWaitScreenProps {
  readonly envelope: PreviewEnvelope;
}

export function PostPaymentWaitScreen({ envelope }: PostPaymentWaitScreenProps) {
  const copy = STRINGS.book.postPaymentWait;
  const coverImageUrl = envelope.cover.imageUrl;
  const tiles = envelope.previewTiles;

  return (
    <div className="flex flex-col items-center gap-6 pt-6 pb-8">
      <div className="flex max-w-[520px] flex-col items-center gap-4 text-center">
        <h1 className="text-2xl font-semibold text-dream-navy">{copy.heading}</h1>
        <p className="text-base leading-relaxed text-dream-navy">{copy.body1}</p>
        <p className="text-base leading-relaxed text-dream-navy/80">{copy.body2}</p>
      </div>

      {/* Anchor image — "Eure Figuren" caption below, matching the
          Screen 4 tile caption. Only renders when the URL is set;
          empty case skips the block silently. */}
      {coverImageUrl !== null && (
        <figure className="flex w-full max-w-[280px] flex-col items-center gap-2">
          <img
            src={coverImageUrl}
            alt=""
            aria-hidden
            className="w-full rounded-lg border border-book-border object-cover"
          />
          <figcaption className="text-xs font-medium text-book-text-secondary">
            {STRINGS.book.preview.thumbCoverLabel}
          </figcaption>
        </figure>
      )}

      {/* 2×2 grid of the four preview tiles. Same size + same captions
          as Screen 4's TileRow. Grid columns fixed to 2 so the layout
          reads identically on phone and desktop (Sebastian: "Same
          layout on phone and desktop, everything stacked vertically —
          no left/right split"). */}
      {tiles.length > 0 && (
        <div className="grid w-full max-w-[520px] grid-cols-2 gap-4">
          {tiles.map((tile) => {
            const imageUrl = tile.rawImageUrl ?? tile.composedImageUrl;
            const caption = STRINGS.book.preview.thumbPageLabel.replace(
              '{pageNo}',
              tile.pageNo.toString(),
            );
            return (
              <figure key={tile.pageNo} className="flex flex-col items-center gap-2">
                {imageUrl !== null ? (
                  <img
                    src={imageUrl}
                    alt=""
                    aria-hidden
                    className="aspect-square w-full rounded-lg border border-book-border object-cover"
                  />
                ) : (
                  <div
                    aria-hidden
                    className="aspect-square w-full rounded-lg border border-dashed border-book-border bg-book-card/60"
                  />
                )}
                <figcaption className="text-xs font-medium text-book-text-secondary">
                  {caption}
                </figcaption>
              </figure>
            );
          })}
        </div>
      )}

      {/* Block 6.7 review correction (Sebastian ruling 2026-09-09) —
          two-stage indicator driven by envelope.status. `qa_running`
          → Stage 2 (Kontrollieren) is bold + spinner; every other
          post-payment status shows Stage 1 (Zeichnen) bold + spinner
          with Stage 2 thin below. Real backend signal, no timer. The
          switch fires when the backend flips rendering_full →
          qa_running (or paid → rendering_full → qa_running along the
          full sequence). */}
      <StageIndicator status={envelope.status} />
    </div>
  );
}

interface StageIndicatorProps {
  readonly status: PreviewEnvelope['status'];
}

function StageIndicator({ status }: StageIndicatorProps) {
  // Stage classification: qa_running is Stage 2; every other status
  // this screen renders under (checkout_pending on /erfolg, paid,
  // rendering_full) is Stage 1. Any status that shouldn't reach
  // this screen at all (customer_review + later) also falls to
  // Stage 1 defensively — this component only renders as a body
  // fragment, so the belt-and-braces default is Zeichnen, not a
  // throw.
  const isStage2 = status === 'qa_running';
  return (
    <ul className="flex flex-col items-center gap-2 text-sm">
      <StageLine
        text={STRINGS.book.postPaymentWait.stageRendering}
        active={!isStage2}
      />
      <StageLine
        text={STRINGS.book.postPaymentWait.stageQa}
        active={isStage2}
      />
    </ul>
  );
}

function StageLine({ text, active }: { readonly text: string; readonly active: boolean }) {
  return (
    <li
      className={
        active
          ? 'flex items-center gap-2 font-semibold text-dream-navy'
          : 'text-dream-navy/50'
      }
    >
      <span>{text}</span>
      {active && <Loader2 aria-hidden className="h-4 w-4 animate-spin text-dream-navy/70" />}
    </li>
  );
}
